import type { Mode } from "@eldrun/content";
import type { FbmOptions } from "./noise.ts";

/** Mundo quadrado centrado na origem: x, z ∈ [−WORLD_HALF, WORLD_HALF]. */
export const WORLD_SIZE = 2048;
export const WORLD_HALF = WORLD_SIZE / 2;
export const SEA_LEVEL = 0;
/** Água até esta profundidade dá pé (mangue); mais funda, o jogador nada. */
export const SHALLOW_WATER_DEPTH = 1.2;

/** Heightfield físico: 1025 × 1025 amostras a cada 2 m (ADR 0004/0006). */
export const HEIGHT_SPACING = 2;
export const HEIGHT_SIDE = WORLD_SIZE / HEIGHT_SPACING + 1;

/** Mapa de planejamento: 257 × 257 células de 8 m (biomas, POIs, minimapa). */
export const PLAN_SPACING = 8;
export const PLAN_SIDE = WORLD_SIZE / PLAN_SPACING + 1;

export const CHUNK_SIZE = 128;
export const CHUNKS_PER_SIDE = WORLD_SIZE / CHUNK_SIZE;

export interface TerrainParams {
  /** Raio de referência da ilha, em metros. */
  radius: number;
  /** Área emersa alvo, em m²; o nível do mar é ajustado para atingi-la. */
  targetLandArea: number;
  continental: FbmOptions;
  warp: FbmOptions & { amplitude: number };
  ridge: FbmOptions & { weight: number };
  ridgeMask: FbmOptions;
  moisture: FbmOptions;
  detail: FbmOptions & { amplitude: number };
  /** Início da queda de borda, em fração do raio. */
  falloffStart: number;
}

const BASE: Omit<TerrainParams, "radius" | "targetLandArea"> = {
  continental: { octaves: 5, wavelength: 900, lacunarity: 2, gain: 0.5 },
  warp: { octaves: 3, wavelength: 520, lacunarity: 2, gain: 0.5, amplitude: 170 },
  ridge: { octaves: 4, wavelength: 420, lacunarity: 2.1, gain: 0.5, weight: 0.32 },
  ridgeMask: { octaves: 2, wavelength: 1200, lacunarity: 2, gain: 0.5 },
  moisture: { octaves: 3, wavelength: 700, lacunarity: 2, gain: 0.5 },
  detail: { octaves: 2, wavelength: 40, lacunarity: 2, gain: 0.5, amplitude: 1.2 },
  falloffStart: 0.3,
};

const MUTIRAO_RADIUS = 880;
/** Treta: mesma ilha em escala 0,7 (doc 07, M1). */
const TRETA_SCALE = 0.7;

export function terrainParams(mode: Mode): TerrainParams {
  const scale = mode === "treta" ? TRETA_SCALE : 1;
  return {
    ...BASE,
    radius: MUTIRAO_RADIUS * scale,
    targetLandArea: 1_400_000 * scale * scale,
  };
}

/** Parâmetros conservadores usados quando todas as tentativas falham na validação. */
export function conservativeParams(mode: Mode): TerrainParams {
  const p = terrainParams(mode);
  return {
    ...p,
    warp: { ...p.warp, amplitude: p.warp.amplitude * 0.4 },
    ridge: { ...p.ridge, weight: p.ridge.weight * 0.8 },
    falloffStart: 0.5,
  };
}

/** Tentativas de validação antes de recorrer aos parâmetros conservadores. */
export const MAX_ATTEMPTS = 8;
